import React, { useEffect, useState, useRef } from "react";
import { logResponse } from "../utils/logger";
import iconPay from "../assets/img/icon_pay.svg";
import iconCaution from "../assets/img/icon_caution.svg";
import { fetchGetCustomer, fetchUpdateCardExpiry } from "../api";
import SubmitButton from "../components/SubmitButton";

const extractCardsFromCustomer = (customer) => {
  const cards = [];
  let index = 1;

  while (customer[`card_brand_${index}`] !== undefined) {
    const brand = customer[`card_brand_${index}`];
    const digits = customer[`card_last_digits_${index}`];
    const expiry = customer[`card_expiry_ym_${index}`];
    const expired = customer[`is_card_expired_${index}`];
    const memberId = customer[`member_id_${index}`];

    let year = "";
    let month = "";

    if (expiry) {
      // 例: "2026年11月" / "26年11月" / "2026年 11月" など全部対応
      const match = expiry.match(/(\d{2,4})年\s*(\d{1,2})月/);
      if (match) {
        year = match[1];
        month = match[2].padStart(2, "0");
      }
    }

    if (brand || digits || expiry) {
      cards.push({
        brand,
        card_last_digits: digits,
        expiry_year: year,
        expiry_month: month,
        is_expired: expired === 1,
        member_id: memberId,
      });
    }

    index++;
  }

  return cards;
};

// ★ 年を4桁に正規化（例: "26" → 2026）
const normalizeYear = (yy) => {
  const num = Number(yy);
  if (num < 100) {
    return 2000 + num;
  }
  return num;
};

// ★ 有効期限ステータス判定
const getExpiryStatus = (year, month) => {
  const expYear = normalizeYear(year);
  const expMonth = Number(month);

  if (!expYear || !expMonth) return "expired";

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  // 期限切れ（過去）
  if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
    return "expired";
  }

  // 今月
  if (expYear === currentYear && expMonth === currentMonth) {
    return "thisMonth";
  }

  // 来月
  if (
    (expYear === currentYear && expMonth === currentMonth + 1) ||
    (expYear === currentYear + 1 && currentMonth === 12 && expMonth === 1)
  ) {
    return "nextMonth";
  }

  return "valid";
};

const CardInfo = () => {
  const [cards, setCards] = useState([]);
  const formRefs = useRef([]);
  const resultRef = useRef();
  const [messageMap, setMessageMap] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [editedIndex, setEditedIndex] = useState(null);

  useEffect(() => {
    const access_token = localStorage.getItem("access_token");
    if (!access_token) return;

    fetchGetCustomer(access_token).then((customer) => {
      const extracted = extractCardsFromCustomer(customer);
      setCards(extracted);

      setTimeout(() => {
        window.dispatchEvent(new Event("DOMContentLoaded"));
      }, 0);
    });
  }, []);

  const handleEditClick = (index) => {
    setEditedIndex(index);
  };

  const handleClick = async (event, formEl, index) => {
    event.preventDefault();
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const access_token = localStorage.getItem("access_token");
      if (!access_token) return;

      const formData = new FormData(formEl);
      const data = Object.fromEntries(formData.entries());

      const response = await fetchUpdateCardExpiry(access_token, data);
      logResponse("token:", access_token);
      logResponse("req:", data);
      logResponse("res:", response);

      setMessageMap((prev) => ({
        ...prev,
        [index]: "カード有効期限を更新しました。",
      }));

      // 更新した有効期限（年）をローカルstateへ反映し、期限ステータスを再計算する。
      // これをしないと card.expiry_year が更新前のまま残り、「有効期限が切れています」の
      // 赤字警告が消えず、更新できていないように見えてしまう。
      const newYear = String(normalizeYear(data.card_expiry_year));
      setCards((prev) =>
        prev.map((c, i) =>
          i === index ? { ...c, expiry_year: newYear, is_expired: false } : c
        )
      );
      setEditedIndex(null);

      if (resultRef?.current) {
        resultRef.current.value = JSON.stringify(response, null, 2);
      }
    } catch (err) {
      console.error("更新エラー:", err);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <section className="section cardinfo-section">
      <h2 className="section-title">
        <i>
          <img src={iconPay} alt="" />
        </i>
        クレジットカード登録情報
      </h2>

      <div className="cardinfo-group group-wrapper">
        <div className="card-add">
          <a
            className="underline"
            href="https://www.kotohogi.co.jp/change-pay-credit/"
            target="_blank"
            rel="noopener noreferrer"
          >
            ＋ カードを追加する
          </a>
        </div>

        {cards.map((card, index) => {
          const status = getExpiryStatus(card.expiry_year, card.expiry_month);

          return (
            <div className="cardinfo" key={index}>
              <form ref={(el) => (formRefs.current[index] = el)}>
                <div className="head flex middle between">
                  <div className="brand">{card.brand}</div>
                  <div className="cardno">{card.card_last_digits}</div>
                </div>

                <div className="body">
                  有効期限
                  <span className="expiry">

                    {editedIndex === index ? (
                      <select
                        className="year"
                        defaultValue={card.expiry_year.toString().slice(-2)}
                        onChange={(e) => {
                          const hiddenInput = formRefs.current[index]?.querySelector("input[name='card_expiry_year']");
                          if (hiddenInput) hiddenInput.value = e.target.value;
                        }}
                      >
                        {Array.from({ length: 21 }, (_, i) => {
                          const y = new Date().getFullYear() - 10 + i;
                          const yy = y.toString().slice(-2);
                          return (
                            <option key={yy} value={yy}>
                              {yy}
                            </option>
                          );
                        })}
                      </select>
                    ) : (
                      <span className="year">{card.expiry_year}</span>
                    )}

                    <span className="unit">年</span>
                    <span className="month">{card.expiry_month}</span>
                    <span className="unit">月</span>
                  </span>

                  {editedIndex !== index && (
                    <button
                      type="button"
                      className="edit link underline"
                      style={{ color: status !== "valid" ? "red" : "inherit" }}
                      onClick={() => handleEditClick(index)}
                    >
                      編集
                    </button>
                  )}

                  {/* ▼ 期限ステータス表示 */}
                  {status === "expired" && (
                    <div className="caution">
                      <i><img src={iconCaution} alt="" /></i>
                      有効期限が切れています
                    </div>
                  )}

                  {status === "thisMonth" && (
                    <div className="caution">
                      <i><img src={iconCaution} alt="" /></i>
                      今月内に有効期限が切れます
                    </div>
                  )}

                  {status === "nextMonth" && (
                    <div className="caution">
                      <i><img src={iconCaution} alt="" /></i>
                      来月に有効期限が切れます
                    </div>
                  )}

                  <input type="hidden" name="member_id" value={card.member_id} />
                  <input
                    type="hidden"
                    name="card_expiry_year"
                    value={card.expiry_year.toString().slice(-2)}
                  />

                  {messageMap[index] && (
                    <div className="success-message mt20">
                      <p style={{ color: "green", fontWeight: "bold" }}>{messageMap[index]}</p>
                    </div>
                  )}

                  <div className="update btn-green" style={{ display: editedIndex === index ? "block" : "none" }}>
                    <SubmitButton
                      type="button"
                      onClick={(e) => handleClick(e, formRefs.current[index], index)}
                      isSubmitting={isSubmitting}
                      label="有効期限を変更する"
                      className="btn-green"
                      wrap={false}
                    />
                  </div>
                </div>
              </form>
            </div>
          );
        })}
      </div>

      <textarea ref={resultRef} readOnly rows="6" style={{ display: "none" }} />
    </section>
  );
};

export default CardInfo;
